import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import ModelLogo from '@/components/models/ModelLogo';
import { LOCAL_MODEL_ICON_OPTIONS, getModelIconPath } from '@/lib/modelIcons';

interface ProviderLogoUploadDialogProps {
  providerName: string;
  currentIconFilename: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (iconFilename: string) => void;
}

const ProviderLogoUploadDialog: React.FC<ProviderLogoUploadDialogProps> = ({
  providerName,
  currentIconFilename,
  isOpen,
  onOpenChange,
  onSave,
}) => {
  const [selectedFilename, setSelectedFilename] = useState(currentIconFilename);

  useEffect(() => {
    setSelectedFilename(currentIconFilename);
  }, [currentIconFilename]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Choose local icon for {providerName}</DialogTitle>
          <DialogDescription>
            Icons now come from local app assets instead of uploading them to storage.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-3 rounded-xl border bg-muted/30 p-3">
            <ModelLogo
              src={getModelIconPath(selectedFilename)}
              alt={providerName}
              sizeClass="h-12 w-12"
              fallback={<span className="text-xs font-medium">{providerName.slice(0, 2).toUpperCase()}</span>}
            />
            <div>
              <p className="text-sm font-medium">Selected asset</p>
              <p className="text-sm text-muted-foreground">{selectedFilename || 'None selected'}</p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {LOCAL_MODEL_ICON_OPTIONS.map((option) => {
              const src = getModelIconPath(option.filename);
              const isSelected = option.filename === selectedFilename;

              return (
                <button
                  key={option.filename}
                  type="button"
                  onClick={() => setSelectedFilename(option.filename)}
                  className={`rounded-xl border p-3 text-left transition hover:border-primary/50 hover:bg-accent/40 ${isSelected ? 'border-primary bg-primary/5' : 'border-border bg-background'}`}
                >
                  <div className="flex items-center gap-3">
                    <ModelLogo
                      src={src}
                      alt={option.label}
                      sizeClass="h-10 w-10"
                      fallback={<span className="text-xs font-medium">{option.label.slice(0, 2).toUpperCase()}</span>}
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{option.label}</p>
                      <p className="truncate text-xs text-muted-foreground">{option.filename}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(selectedFilename)} disabled={!selectedFilename}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProviderLogoUploadDialog;